import {
  AdminInput,
  AdminSelect,
  Div,
  formatEIN,
  formatSSN,
  isPartner,
  P,
  TaxClassification,
} from '@vgrubs/components';
import { BusinessType } from '../../hooks/useWalletV2';

export const BusinessInfo = ({
  businessName,
  setBusinessName,
  ssn,
  setSsn,
  ein,
  setEin,
  taxClassification,
  setTaxClassification,
  businessType,
  setBusinessType,
}: {
  businessName: string;
  setBusinessName: (value: string) => void;
  ssn: string;
  setSsn: (value: string) => void;
  ein: string;
  setEin: (value: string) => void;
  taxClassification: TaxClassification;
  setTaxClassification: (value: TaxClassification) => void;
  businessType: BusinessType;
  setBusinessType: (value: BusinessType) => void;
}) => {
  const renderSSNorEIN = () => {
    if (
      businessType === BusinessType.soleProprietor ||
      (businessType === BusinessType.llc &&
        taxClassification === TaxClassification.singleMemberLlc)
    ) {
      return (
        <AdminInput
          label="SSN"
          placeholder="XXX-XX-XXXX"
          value={ssn || ''}
          onChange={({ target }) => setSsn(formatSSN(target.value))}
          $textCenter
        />
      );
    } else
      return (
        <AdminInput
          label="EIN"
          placeholder="XX-XXXXXXX"
          value={ein}
          onChange={({ target }) => setEin(formatEIN(target.value))}
          $textCenter
        />
      );
  };

  const renderTaxClassification = () => {
    if (businessType === BusinessType.llc) {
      return (
        <AdminSelect
          $textCenter
          label={`How is ${isPartner ? 'your' : 'this account'} LLC taxed?`}
          value={taxClassification || ''}
          options={llcOptionsSelect}
          onChange={({ target }) => {
            setTaxClassification(target.value as TaxClassification);
            setSsn('');
            setEin('');
          }}
        />
      );
    } else if (businessType === BusinessType.corporation) {
      return (
        <AdminSelect
          $textCenter
          label="What type of corporation?"
          value={taxClassification || ''}
          options={corporationOptionsSelect}
          onChange={({ target }) => {
            setTaxClassification(target.value as TaxClassification);
            setSsn('');
            setEin('');
          }}
        />
      );
    } else {
      return null;
    }
  };

  const handleChangeBusinessType = (value: BusinessType) => {
    setSsn('');
    setEin('');
    switch (value) {
      case BusinessType.llc:
        setTaxClassification(TaxClassification.singleMemberLlc);
        break;
      case BusinessType.corporation:
        setTaxClassification(TaxClassification.sCorporation);
        break;
      case BusinessType.partnership:
        setTaxClassification(TaxClassification.partnership);
        break;
      case BusinessType.trust:
        setTaxClassification(TaxClassification.trust);
        break;
      case BusinessType.estate:
        setTaxClassification(TaxClassification.estate);
        break;
      default:
        setTaxClassification(TaxClassification.soleProprietor);
    }

    setBusinessType(value);
  };

  return (
    <Div $flex $column $gap="24px">
      <P $m="0">Tell us about {isPartner ? 'your' : 'this account'} company</P>
      <AdminInput
        label="Legal Business Name"
        placeholder="Enter Here"
        value={businessName}
        onChange={({ target }) => setBusinessName(target.value)}
        $textCenter
      />

      <AdminSelect
        $textCenter
        label="Business Type"
        value={businessType || ''}
        options={businessTypeOptionsSelect}
        onChange={({ target }) => {
          handleChangeBusinessType(target.value as BusinessType);
        }}
      />
      {renderTaxClassification()}
      {renderSSNorEIN()}
    </Div>
  );
};

const businessTypeOptions: {
  name: string;
  value: BusinessType;
}[] = [
  { name: 'Sole Proprietor', value: BusinessType.soleProprietor },
  { name: 'LLC', value: BusinessType.llc },
  { name: 'Corporation', value: BusinessType.corporation },
  { name: 'Partnership', value: BusinessType.partnership },
  { name: 'Trust', value: BusinessType.trust },
  { name: 'Estate', value: BusinessType.estate },
];

const businessTypeOptionsSelect = (
  <optgroup label="Select Business Type">
    {businessTypeOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const llcOptions: {
  name: string;
  value: TaxClassification;
}[] = [
  { name: 'Single Member', value: TaxClassification.singleMemberLlc },
  { name: 'Partnership', value: TaxClassification.llcPartnership },
  { name: 'S-Corporation', value: TaxClassification.llcSCorporation },
  { name: 'C-Corporation', value: TaxClassification.llcCCorporation },
];

const llcOptionsSelect = (
  <optgroup label="Select Business Type">
    {llcOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const corporationOptions: {
  name: string;
  value: TaxClassification;
}[] = [
  { name: 'S-Corp', value: TaxClassification.sCorporation },
  { name: 'C-Corp', value: TaxClassification.cCorporation },
];

const corporationOptionsSelect = (
  <optgroup label="Select Business Type">
    {corporationOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);
